import { useEffect } from "react";
import { esFichaDeUnCoche } from "../utils/fichaPublica";
import {
  getAuthSessionJson,
  getUserAlertsJson,
  getUserPreferencesJson,
  getUserSavedComparisonsJson,
} from "../utils/apiClient";
import {
  readAuthUser,
  readMarketAlerts,
  readMarketAlertStatus,
  readQuestionnaireDraft,
  readSavedComparisons,
  writeSavedComparisons,
} from "../utils/storage";

export function useAppBootstrap({
  themeStorageKey,
  setThemeMode,
  setSavedComparisons,
  setMarketAlerts,
  setMarketAlertStatus,
  setQuestionnaireDraft,
  entraEnLaCuenta,
  saleDeLaCuenta,
  setAuthRequired,
  setAuthDialogMode,
  setShowConsentReview,
  /** Se avisa en cuanto se sabe si hay sesión, aunque la respuesta sea que no. */
  setSesionComprobada,
}) {
  useEffect(() => {
    const savedAuthUser = readAuthUser();
    const persistedTheme = typeof window !== "undefined" ? window.localStorage.getItem(themeStorageKey) : "";
    const PUBLIC_PATHS = ["/", "/aviso-legal", "/politica-privacidad", "/politica-cookies", "/terminos-condiciones", "/politica-comunicaciones", "/politica-experian", "/condiciones-experian"];

    // Capture landing data from URL on first visit and persist for registration
    try {
      const existing = window.localStorage.getItem("ma.landing");
      if (!existing) {
        const urlParams = new URLSearchParams(window.location.search);
        const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
        const utms = {};
        utmKeys.forEach((k) => { const v = urlParams.get(k); if (v) utms[k] = v; });

        // Collect all non-UTM params as affiliate data
        const affiliateData = {};
        urlParams.forEach((v, k) => {
          if (!utmKeys.includes(k)) affiliateData[k] = v;
        });

        const landing = {
          utms,
          affiliateData: Object.keys(affiliateData).length > 0 ? affiliateData : null,
          referer: document.referrer || "",
          landingUrl: window.location.href,
          language: navigator.language || "",
        };
        window.localStorage.setItem("ma.landing", JSON.stringify(landing));
      }
    } catch {}
    const isPublicRoute =
      typeof window !== "undefined" &&
      (PUBLIC_PATHS.some((p) => window.location.pathname === p || window.location.pathname.startsWith(p + "/")) ||
        // La ficha de un coche, y la dirección corta de los portales que lleva
        // a ella: sin esto el arranque levanta el diálogo de sesión por encima
        // y el comprador que viene de coches.net no pasa de ahí.
        esFichaDeUnCoche(window.location.pathname));

    if (persistedTheme === "dark" || persistedTheme === "light") {
      setThemeMode(persistedTheme);
    }

    setSavedComparisons(readSavedComparisons());
    /* Los seis del panel se siembran en `useUserMobilitySync`, que es quien
       los trae del servidor. */
    setMarketAlerts(readMarketAlerts());
    setMarketAlertStatus(readMarketAlertStatus());
    setQuestionnaireDraft(readQuestionnaireDraft());

    /* Lo guardado en el navegador, que es lo que evita ver la portada un
       instante al recargar. Sin correo, `entra` deja «no hay nadie». */
    entraEnLaCuenta(savedAuthUser);
    /*
     * Ya se sabe.
     *
     * A partir de aquí «no hay sesión» es una respuesta y no una pregunta sin
     * contestar, y quien la mire puede fiarse. Antes, el guardia de
     * `/mis-coches` corría en esta misma pasada y veía el valor inicial —falso—
     * con la sesión guardada: le pedía la contraseña a alguien que ya estaba
     * dentro, justo al llegar desde «lo que te falta» de su encargo.
     */
    if (setSesionComprobada) setSesionComprobada(true);

    // No cached user → require login immediately (sync, no flash)
    let loPedimosNosotros = false;
    if (!savedAuthUser?.email && !isPublicRoute) {
      loPedimosNosotros = true;
      setAuthRequired(true);
      setAuthDialogMode("login");
    }

    /* Aquí se leía el consentimiento de cookies y se encendía el aviso. No
       tenía que ver con arrancar la sesión ni con el catálogo: estaba aquí
       porque era donde ya se leía `localStorage`. Ahora lo hace
       `useAppPreferences`, que es quien tiene ese estado. */

    void (async () => {
      try {
        const { data } = await getAuthSessionJson();
        const sessionUser = data?.authenticated ? data?.user : null;

        if (sessionUser?.email) {
          entraEnLaCuenta(sessionUser);

          /*
           * Si la contraseña la hemos pedido nosotros y el servidor dice que ya
           * está dentro, se retira la petición.
           *
           * No se retiraba. Sin la nota de este lado —y había un borrado que se
           * la llevaba en cada carga— se levantaba el diálogo, el servidor
           * contestaba que la sesión estaba abierta, y el diálogo se quedaba ahí
           * encima, sin aspa para cerrarlo, sobre una página en la que ya se
           * había entrado. La única salida era escribir la contraseña.
           *
           * Solo se retira lo que hemos pedido nosotros: si el diálogo lo abrió
           * ella, se queda.
           */
          if (loPedimosNosotros) {
            loPedimosNosotros = false;
            setAuthRequired(false);
            setAuthDialogMode("");
          }

          // Sync backend data into state; localStorage becomes fallback cache.
          void (async () => {
            try {
              const [savedRes, alertsRes, prefsRes] = await Promise.allSettled([
                getUserSavedComparisonsJson(),
                getUserAlertsJson(),
                getUserPreferencesJson(),
              ]);

              if (savedRes.status === "fulfilled" && savedRes.value?.data?.ok) {
                const comparisons = Array.isArray(savedRes.value.data.comparisons)
                  ? savedRes.value.data.comparisons
                  : [];
                writeSavedComparisons(comparisons);
                setSavedComparisons(comparisons);
              }

              if (alertsRes.status === "fulfilled" && alertsRes.value?.data?.ok) {
                const alerts = Array.isArray(alertsRes.value.data.alerts)
                  ? alertsRes.value.data.alerts
                  : [];
                const alertStatus = alertsRes.value.data.alertStatus || {};
                setMarketAlerts(alerts);
                setMarketAlertStatus(alertStatus);
              }

              if (prefsRes.status === "fulfilled" && prefsRes.value?.data?.ok && prefsRes.value.data.preferences) {
                try {
                  window.localStorage.setItem(
                    "movilidad-advisor.userDashboard.preferences.v1",
                    JSON.stringify(prefsRes.value.data.preferences)
                  );
                } catch {}
              }
            } catch {
              // Backend sync failed; keep localStorage state as fallback.
            }
          })();

          return;
        }

        saleDeLaCuenta();
        // Session expired → require login again (skip on public/legal pages)
        if (!isPublicRoute) {
          setAuthRequired(true);
          setAuthDialogMode("login");
        }
      } catch {
        // If backend session check fails, keep local state as fallback.
      }
    })();
  }, [
    entraEnLaCuenta,
    saleDeLaCuenta,
    setSesionComprobada,
    setMarketAlertStatus,
    setMarketAlerts,
    setQuestionnaireDraft,
    setSavedComparisons,
    setThemeMode,
                themeStorageKey,
    setAuthRequired,
    setAuthDialogMode,
    setShowConsentReview,
  ]);
}
